/**
 * The one coercion between a stored date and what a date input will accept.
 *
 * `<input type="date">` accepts only `YYYY-MM-DD` and `<input
 * type="datetime-local">` only `YYYY-MM-DDTHH:MM`. Anything else is not an
 * error — the browser renders the field EMPTY and logs a warning nobody sees.
 * Backends here hand dates back in several shapes: a DATE column stored in a
 * TIMESTAMP reads as `2026-09-15T00:00:00`, a DATETIME carries microseconds
 * and a timezone offset, and a default may still be an unresolved
 * `today()` token. Each one silently blanks the box, and a blank box that is
 * then saved CLEARS a value that was there.
 *
 * It lived in two places before this module — `FormRenderer` and
 * `InlineCellEditor`, whose copy opened "Mirror FormRenderer.formatForInput",
 * a duplicate announcing itself — and the document form had no copy at all,
 * which is how a mandatory agreement date came to be invisible in the form
 * while printing with a time in the document beside it.
 *
 * `dms/MetadataFieldInput.toInputValue` deliberately stays where it is: it
 * coerces by the field's DECLARED type rather than by an input type and also
 * settles BOOLEAN, so folding it in here would widen this module into "every
 * value coercion" and put a boolean rule in a date file. Its date half already
 * takes the same timezone-safe text prefix this does.
 */

/** `'date'` | `'datetime-local'`; any other type passes the value through. */
export function formatForInput(value: unknown, type: string): string {
  if (value == null || value === '') return ''
  if (type !== 'date' && type !== 'datetime-local') return String(value)
  const s = String(value)
  // Already in the target shape.
  if (type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  if (type === 'datetime-local'
      && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(s)) {
    return s
  }
  // A full timestamp whose DATE half is all a date input wants. Taken as text
  // rather than through `new Date()`, because parsing `2026-09-15T00:00:00Z`
  // and reading it back in a timezone behind UTC yields the 14th — a stored
  // date must never shift by a day just because it was displayed.
  if (type === 'date' && /^\d{4}-\d{2}-\d{2}[T ]/.test(s)) return s.slice(0, 10)
  // Function-call tokens (today(), now(), …) are server-resolved; passing one
  // to a date input earns a "does not conform to required format" warning.
  if (/^\s*[A-Za-z_][A-Za-z0-9_]*\s*\(.*\)\s*$/.test(s)) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  if (type === 'date') return stamp
  return `${stamp}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/*
 * ── Zoned datetimes (TIMESTAMPTZ) ─────────────────────────────────────────
 *
 * A TIMESTAMPTZ comes back as an INSTANT (`2026-10-01T14:13:00+00:00`), and
 * `formatForInput` shows it in the browser's local time — 19:43 in IST. The
 * box's own value has no zone, so sending it back as-is stored the LOCAL
 * wall-clock time as if it were UTC: pick 19:43, save, reload, and read 01:13
 * the next day; pick 14:13 and it reads 19:43. Whatever was shown local must
 * go back local, so a zoned field's edit carries the browser's offset.
 */

/** True when a stored value names its own zone (`Z` or `±HH[:MM]`). */
export function hasZone(value: unknown): boolean {
  if (typeof value !== 'string') return false
  return /[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?\s*(?:Z|[+-]\d{2}(?::?\d{2})?)$/i
    .test(value.trim())
}

/**
 * `YYYY-MM-DDTHH:MM` (local wall-clock) → the same moment with the browser's
 * offset for THAT date, so a DST change between now and then is honoured.
 * Anything else passes through.
 */
export function withLocalZone(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(v)
  if (!m) return v
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  if (isNaN(d.getTime())) return v
  const off = -d.getTimezoneOffset()
  const sign = off >= 0 ? '+' : '-'
  const abs = Math.abs(off)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${v}:00${sign}${p(Math.floor(abs / 60))}:${p(abs % 60)}`
}

/*
 * ── Typed entry ───────────────────────────────────────────────────────────
 *
 * `<input type="date">` and `<input type="datetime-local">` are segmented
 * boxes, and a segmented box reports NOTHING until every one of its segments
 * is complete: the element's value stays `''` and no `input` event is fired
 * at all. So a person who types 15/09 and tabs away, or who types a date into
 * a DATETIME field without also typing the time, has entered nothing — and is
 * told nothing, because the box still shows the digits they typed. Picking the
 * same date from the calendar works, because the picker commits every segment
 * at once. That asymmetry ("only the calendar works") is what `DateField`
 * exists to remove: it types as text and parses here.
 *
 * Verified against Chrome: typing only the date half of a `datetime-local`
 * leaves `el.value === ''` with zero `input` events; typing two of a `date`
 * input's three segments does the same.
 *
 * The entry format is day-first `dd/mm/yyyy` — stated in the placeholder
 * rather than inferred from the browser locale, because the same application
 * is used from browsers whose native order disagrees, and a date silently
 * read in the other order is worse than one refused.
 */

/** What the entry box shows for a stored value. '' when there is none. */
export function formatForDisplay(value: unknown, type: string): string {
  const iso = formatForInput(value, type)
  if (!iso) return ''
  const [d, t] = iso.split('T')
  const [y, m, day] = d.split('-')
  if (!y || !m || !day) return ''
  const date = `${day}/${m}/${y}`
  if (type !== 'datetime-local') return date
  return t ? `${date} ${t.slice(0, 5)}` : `${date} 00:00`
}

const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
]

/**
 * Two-digit years pivot at 50: 26 → 2026, 74 → 1974. THREE digits is not a
 * year anybody means — it is `2026` caught halfway through being typed, and
 * reading it as the year 202 is the silent mis-store this whole field exists
 * to prevent. `null` = keep waiting.
 */
function fullYear(y: number, digits: number): number | null {
  if (digits === 3) return null
  if (digits > 2) return y
  return y < 50 ? 2000 + y : 1900 + y
}

function valid(y: number, m: number, d: number): boolean {
  if (y < 1 || m < 1 || m > 12 || d < 1) return false
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return d <= last
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0')

/** Parse the time tail: `HH:mm`, `HH:mm:ss`, `h:mm am/pm`, or `HHmm`. */
function parseTime(raw: string): { h: number; mi: number } | null {
  const s = raw.trim().toLowerCase()
  if (!s) return { h: 0, mi: 0 }
  const m = s.match(/^(\d{1,2})(?::?(\d{2}))?(?::\d{2})?\s*(am|pm)?$/)
  if (!m) return null
  let h = Number(m[1])
  const mi = m[2] == null ? 0 : Number(m[2])
  const ap = m[3]
  if (ap) {
    if (h < 1 || h > 12) return null
    h = ap === 'pm' ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h)
  }
  // A bare 4-digit block is HHmm ("0930"), not an hour of 930.
  if (!m[2] && m[1].length === 4) return null
  if (h > 23 || mi > 59) return null
  return { h, mi }
}

/**
 * Parse what somebody typed into the canonical value the engine stores
 * (`YYYY-MM-DD`, or `YYYY-MM-DDTHH:MM` for a datetime). `null` = not a date
 * yet, which is not an error while they are still typing; `''` = cleared.
 *
 * Accepted: `15/9/2026`, `15-09-26`, `15.9.2026`, `15092026`, `150926`,
 * `2026-09-15` (ISO, so a pasted or scripted value still lands), and
 * `15 Sep 2026` / `15-september-2026`. A datetime may carry a time tail;
 * without one it is midnight, which is the whole point — a typed date alone
 * must be able to commit.
 */
export function parseTypedDate(text: string, type: string): string | null | '' {
  const raw = (text || '').trim()
  if (!raw) return ''
  const wantsTime = type === 'datetime-local'

  // The whole thing as a date first, so `15 Sep 2026` is not read as the date
  // `15 Sep` followed by the time `2026`.
  let day = parseDatePart(raw)
  let timePart = ''
  if (!day) {
    const split = raw.match(/^(.*?)[T\s]+([\d:]{1,8}\s*(?:[ap]m)?)$/i)
    if (!split) return null
    day = parseDatePart(split[1].trim())
    timePart = split[2].trim()
    if (!day) return null
  }

  const stamp = `${pad(day.y, 4)}-${pad(day.mo)}-${pad(day.d)}`
  const t = parseTime(timePart)
  // A time that is not one is a typo worth refusing, on either type.
  if (!t) return null
  // A DATE column given `15/09/2026 10:30` keeps the date and drops the time,
  // the way the engine's own DATE coercion does.
  if (!wantsTime) return stamp
  return `${stamp}T${pad(t.h)}:${pad(t.mi)}`
}

/** The date half, in any of the accepted spellings. */
function parseDatePart(s: string): { y: number; mo: number; d: number } | null {
  let y: number
  let mo: number
  let d: number
  let m: RegExpMatchArray | null
  if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/))) {
    // Year-first (ISO). Unambiguous, so it is read exactly as written.
    y = Number(m[1]); mo = Number(m[2]); d = Number(m[3])
  } else if ((m = s.match(/^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{2,4})$/))) {
    d = Number(m[1]); mo = Number(m[2])
    const yy = fullYear(Number(m[3]), m[3].length)
    if (yy == null) return null
    y = yy
    // Day-first is the rule, but `09/15/2026` can only be month-first, and
    // refusing it would lose a date somebody clearly meant.
    if (d > 12 && mo > 12) return null
    if (mo > 12) { const t = d; d = mo; mo = t }
  } else if ((m = s.match(/^(\d{1,2})[-/.\s]([A-Za-z]{3,})[-/.\s](\d{2,4})$/))) {
    d = Number(m[1])
    mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1
    const yy = fullYear(Number(m[3]), m[3].length)
    if (yy == null || mo === 0) return null
    y = yy
  } else if ((m = s.match(/^(\d{2})(\d{2})(\d{4})$/))) {
    d = Number(m[1]); mo = Number(m[2]); y = Number(m[3])
  } else if ((m = s.match(/^(\d{2})(\d{2})(\d{2})$/))) {
    d = Number(m[1]); mo = Number(m[2]); y = fullYear(Number(m[3]), 2) as number
  } else {
    return null
  }
  return valid(y, mo, d) ? { y, mo, d } : null
}

/** The hint shown in an empty entry box. */
export function entryPlaceholder(type: string): string {
  return type === 'datetime-local' ? 'dd/mm/yyyy hh:mm' : 'dd/mm/yyyy'
}
